import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress
} from '@mui/material';
import { DataService } from 'src/services/data-service';
import { useAppDispatch } from 'src/redux/hooks';
import { showAlert } from 'src/redux/alert/alertReducer';

interface RentCycle {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface RentCycleDialogProps {
  open: boolean;
  rentCycle: RentCycle | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface RentCycleFormData {
  name: string;
  description: string;
  active: boolean;
}

const RentCycleDialog: React.FC<RentCycleDialogProps> = ({
  open,
  rentCycle,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState<RentCycleFormData>({
    name: '',
    description: '',
    active: true
  });
  const [errors, setErrors] = useState<Partial<RentCycleFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useAppDispatch();

  const isEditing = !!rentCycle;

  // Reset form when dialog opens/closes or rent cycle changes
  useEffect(() => {
    if (open) {
      if (rentCycle) {
        setFormData({
          name: rentCycle.name,
          description: rentCycle.description,
          active: rentCycle.active
        });
      } else {
        setFormData({
          name: '',
          description: '',
          active: true
        });
      }
      setErrors({});
    }
  }, [open, rentCycle]);

  // Handle input changes
  const handleInputChange = (field: keyof RentCycleFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = field === 'active' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Partial<RentCycleFormData> = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Rent cycle name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Rent cycle name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Rent cycle name cannot exceed 100 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 5) {
      newErrors.description = 'Description must be at least 5 characters';
    } else if (formData.description.trim().length > 500) {
      newErrors.description = 'Description cannot exceed 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const payload: any = {
        name: formData.name.trim(),
        description: formData.description.trim()
      };

      // Add id for updates (active status is not included in update API based on curl example)
      if (isEditing) {
        payload.id = rentCycle!.id;
      }

      const endpoint = isEditing ? 'api/rent-cycles/update' : 'api/rent-cycles/create';
      const response = await DataService.post(endpoint, payload);

      if (response.ok) {
        onSuccess();
      } else {
        // Try to get error message from response
        let errorMessage = `Failed to ${isEditing ? 'update' : 'create'} rent cycle`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.errors) {
            // Handle validation errors
            const validationErrors = Object.values(errorData.errors).flat();
            errorMessage = validationErrors.join(', ');
          }
        } catch (e) {
          // Use default error message
        }

        dispatch(
          showAlert({
            message: errorMessage,
            type: 'error'
          })
        );
      }
    } catch (error) {
      dispatch(
        showAlert({
          message: `Network error. Please check your connection and try again.`,
          type: 'error'
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleClose = () => {
    if (!submitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Typography variant="h6" component="div">
            {isEditing ? 'Edit Rent Cycle' : 'Add New Rent Cycle'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditing
              ? 'Update the rent cycle information below'
              : 'Fill in the details to create a new rent cycle'
            }
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="Rent Cycle Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            disabled={submitting}
            fullWidth
            placeholder="Enter rent cycle name (e.g., 1 Month, 3 Months, 1 Year)"
            variant="outlined"
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            error={!!errors.description}
            helperText={errors.description}
            disabled={submitting}
            fullWidth
            multiline
            rows={4}
            placeholder="Describe the rent cycle and payment frequency"
            variant="outlined"
          />

          {isEditing && (
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleInputChange('active')}
                  disabled={submitting}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formData.active
                      ? 'This rent cycle is currently active and available for use'
                      : 'This rent cycle is inactive and will not be available for selection'
                    }
                  </Typography>
                </Box>
              }
            />
          )}

          {/* Information for new rent cycles */}
          {!isEditing && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: 1,
                border: '1px solid rgba(0, 0, 0, 0.12)'
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                Rent Cycle Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Once created, this rent cycle will be available for selection in rental agreements.
                Common cycles include Monthly, Quarterly, Semi-Annual, and Annual payment schedules.
              </Typography>
            </Box>
          )}
        </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={submitting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : null}
          >
            {submitting
              ? `${isEditing ? 'Updating' : 'Creating'}...`
              : `${isEditing ? 'Update' : 'Create'} Rent Cycle`
            }
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RentCycleDialog;